// Story ▸ Chronology (StoryCharts.swift's `ChronologyChart`): scenes on a calendar axis in the order they happen, in
// lanes by POV, setting or character (with characters, a scene is in the lane of everyone in it), each lane in its
// entity's colour; estimated times faded (and hidden with Show Estimated Times off). Drag a scene to change when it
// happens (snapped to the quarter hour; an estimated one gets a time of its own); choose one to open it. The list under
// the chart is the same scenes in time order.
import { useRef, useState } from 'react';
import { entityCSSColor, type Project } from '@writing-suite/core';
import { sceneTime, type ChronologyGrouping } from '@writing-suite/core/story';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { useWorkspace } from '../../shell/workspace.ts';
import { MenuButton } from '../Menu.tsx';
import { chronologyOf, setStoryPrefs, snapQuarter, storyScenesOf, useStoryPrefs } from '../../store/story.ts';
import { dateTicks, invert, scale, useWidth } from './charts.ts';
import { StoryEmpty, storyDate, useStoryEmphasis } from './common.tsx';
import { StoryTimeSheet } from './StoryTimeSheet.tsx';
import { SuggestButton } from './TimelinePage.tsx';

const GROUPINGS: { id: ChronologyGrouping; name: string }[] = [
  { id: 'pov', name: 'POV' },
  { id: 'setting', name: 'Setting' },
  { id: 'character', name: 'Character' },
];

export function ChronologyControls() {
  const prefs = useStoryPrefs();
  const name = GROUPINGS.find((g) => g.id === prefs.lanes)!.name;
  return (
    <>
      <MenuButton
        label={`Lanes: ${name}`}
        className="quiet-button"
        title="Lane the scenes by POV, setting or character"
        entries={() => [
          ...GROUPINGS.map((g) => ({ kind: 'radio' as const, label: g.name, checked: prefs.lanes === g.id, onSelect: () => setStoryPrefs({ lanes: g.id }) })),
          { kind: 'separator' as const },
          { kind: 'check' as const, label: 'Show Estimated Times', checked: prefs.estimated, onSelect: () => setStoryPrefs({ estimated: !prefs.estimated }) },
        ]}
      >
        Lanes: {name}
      </MenuButton>
      <SuggestButton />
    </>
  );
}

const LANE = 50;
const TOP = 30;

export function ChronologyPage(props: PageProps) {
  const { project, arrangement, live } = props;
  const ws = useWorkspace();
  const prefs = useStoryPrefs();
  const { faded } = useStoryEmphasis();
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const [drag, setDrag] = useState<{ id: string; from: number; offset: number } | null>(null);
  const scenes = storyScenesOf(project, arrangement);
  const data = chronologyOf(project, scenes, prefs.lanes, prefs.estimated);
  const canWrite = !!ws?.canWrite && !!live;
  const body = !data.scenes.length ? (
    <StoryEmpty>Scenes with a story time appear here in chronological order. Choose Suggest Story Times to fill them in from the text.</StoryEmpty>
  ) : (
    <Chart project={project} width={width} data={data} drag={drag} setDrag={setDrag} canWrite={canWrite}
      onOpen={(id) => ws?.reveal({ fragmentId: id })}
      onMove={(id, ms) => live?.edit((e) => e.setMetadata(id, 'story-time', { type: 'date', value: new Date(ms) }))}
    />
  );
  return (
    <div className="story-page chronology-page" data-lanes={prefs.lanes}>
      <header className="page-head">
        <h1 tabIndex={-1}>Chronology</h1>
        <p className="page-sub">Lanes by {GROUPINGS.find((g) => g.id === prefs.lanes)!.name}</p>
      </header>
      <div className="chart-box" ref={box}>
        {body}
      </div>
      {data.scenes.length ? (
        <>
          <p className="chart-note">{canWrite ? 'Drag a scene on the chart to change when it happens. Choose one in the list to open it.' : 'Choose a scene in the list to open it.'}</p>
          <ol className="chronology-list">
            {data.scenes.map((s) => (
              <li key={s.id} data-scene-id={s.id} data-estimated={s.storyTime === null || undefined}>
                <button type="button" className={'chronology-row' + faded(s.id)} onClick={() => ws?.reveal({ fragmentId: s.id })}>
                  <span className="chronology-when">{storyDate(sceneTime(s)!)}</span>
                  <span className="chronology-title">{s.title}</span>
                  {s.storyTime === null ? <span className="chronology-estimated">estimated</span> : null}
                  <span className="chronology-lanes">{(data.lanes.get(s.id) ?? []).join(', ')}</span>
                </button>
              </li>
            ))}
          </ol>
        </>
      ) : null}
      {prefs.suggesting ? <StoryTimeSheet project={project} live={live} onClose={() => setStoryPrefs({ suggesting: false })} /> : null}
    </div>
  );
}

function Chart({ project, width, data, drag, setDrag, canWrite, onOpen, onMove }: {
  project: Project;
  width: number;
  data: ReturnType<typeof chronologyOf>;
  drag: { id: string; from: number; offset: number } | null;
  setDrag: (d: { id: string; from: number; offset: number } | null) => void;
  canWrite: boolean;
  onOpen: (id: string) => void;
  onMove: (id: string, ms: number) => void;
}) {
  void project;
  const { emphasis } = useStoryEmphasis();
  const lanes = [...new Set(data.bars.map((b) => b.lane))];
  const label = width < 520 ? 90 : 130;
  const first = sceneTime(data.scenes[0])!, last = sceneTime(data.scenes[data.scenes.length - 1])!;
  const span = last - first;
  // Bars at least 1/150 of the span, so an hour-long scene is visible across months.
  const minimum = Math.max(span / 150, 1_800_000);
  const max = last + minimum;
  const x0 = label + 10, x1 = Math.max(x0 + 40, width - 14);
  const x = scale(first, max, x0, x1);
  const toTime = invert(first, max, x0, x1);
  const H = TOP + lanes.length * LANE + 10;
  const axis = dateTicks(first, max, x1 - x0);
  const moved = useRef(false);
  return (
    <svg className="story-chart chronology" width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="group" aria-label="Scenes by story time">
      {axis.ticks.map((t) => (
        <g key={t}>
          <line className="grid-soft" x1={x(t)} x2={x(t)} y1={TOP - 4} y2={H - 6} />
          <text className="axis-label" x={x(t)} y={TOP - 12} textAnchor="middle">
            {axis.label(t)}
          </text>
        </g>
      ))}
      {lanes.map((lane, i) => (
        <g key={lane} data-lane={lane}>
          <line className="grid" x1={x0} x2={x1} y1={TOP + (i + 0.5) * LANE} y2={TOP + (i + 0.5) * LANE} />
          <text className="gutter-label" x={label - 2} y={TOP + (i + 0.5) * LANE + 4} textAnchor="end">
            {lane.length > 18 ? lane.slice(0, 17) + '…' : lane}
          </text>
        </g>
      ))}
      {data.bars.map((b) => {
        const t = sceneTime(b.scene)! + (drag?.id === b.scene.id ? drag.offset : 0);
        const w = Math.max(3, x(t + Math.max(b.scene.duration * 3_600_000, minimum)) - x(t));
        const color = data.colors.get(b.lane);
        const i = lanes.indexOf(b.lane);
        return (
          <rect
            key={b.id}
            className={`chrono-bar${color ? '' : ' none'}${canWrite ? ' draggable' : ''}`}
            data-scene-id={b.scene.id}
            x={x(t)}
            y={TOP + i * LANE + LANE * 0.25}
            width={w}
            height={LANE * 0.5}
            rx={3}
            fill={color ? entityCSSColor(color) : undefined}
            opacity={(drag?.id === b.scene.id ? 0.6 : b.scene.storyTime === null ? 0.35 : 1) * emphasis(b.scene.id)}
            tabIndex={0}
            role="button"
            aria-label={`${b.scene.title}, ${storyDate(sceneTime(b.scene)!)}${b.scene.storyTime === null ? ' (estimated)' : ''}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(b.scene.id);
              }
            }}
            onPointerDown={(e) => {
              if (!canWrite) return;
              moved.current = false;
              e.currentTarget.setPointerCapture(e.pointerId);
              setDrag({ id: b.scene.id, from: e.clientX, offset: 0 });
            }}
            onPointerMove={(e) => {
              if (!drag || drag.id !== b.scene.id) return;
              const dx = e.clientX - drag.from;
              if (Math.abs(dx) >= 4) moved.current = true;
              setDrag({ ...drag, offset: toTime(x0 + dx) - toTime(x0) });
            }}
            onPointerUp={() => {
              const d = drag;
              setDrag(null);
              if (d && moved.current && d.id === b.scene.id) {
                onMove(b.scene.id, snapQuarter(sceneTime(b.scene)! + d.offset));
                return;
              }
              onOpen(b.scene.id);
            }}
            onPointerCancel={() => setDrag(null)}
          >
            <title>{b.scene.title}</title>
          </rect>
        );
      })}
    </svg>
  );
}
